import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';

interface SectionHeaderProps {
  icon: LucideIcon;
  tag: string;       // short uppercase label e.g. "ABOUT"
  index: number;     // section number e.g. 1
  title: string;
  subtitle?: string;
}

export default function SectionHeader({ icon: Icon, tag, index, title, subtitle }: SectionHeaderProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="flex flex-col items-center gap-4 mb-10 sm:mb-12 md:mb-14"
    >
      {/* Mono tag pill */}
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-primary font-mono text-[11px] sm:text-xs font-bold tracking-[0.18em] uppercase select-none">
        <span className="text-primary/40">{String(index).padStart(2, '0')}</span>
        <span className="text-primary/25 mx-0.5">//</span>
        <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
        <span>{tag}</span>
      </div>

      {/* Icon badge + title row */}
      <div className="flex flex-col items-center gap-3">
        {/* Glowing icon badge */}
        <motion.div
          whileHover={{ scale: 1.12, rotate: 6 }}
          transition={{ type: 'spring', stiffness: 300 }}
          className="relative flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/30 shadow-xl shadow-primary/20"
        >
          {/* Inner glow */}
          <div className="absolute inset-0 rounded-2xl bg-primary/10 blur-md" />
          <Icon className="relative z-10 h-7 w-7 sm:h-8 sm:h-8 text-primary drop-shadow-[0_0_8px_rgba(22,255,187,0.8)]" />
        </motion.div>

        {/* Title */}
        <div className="relative text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent bg-[length:200%_auto]">
            {title}
          </h2>
          {/* Animated underline */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
            className="mt-2 h-[3px] w-full rounded-full bg-gradient-to-r from-primary via-secondary to-primary origin-left"
          />
        </div>
      </div>

      {/* Subtitle */}
      {subtitle && (
        <p className="text-center text-sm sm:text-base text-text-muted dark:text-text-light max-w-2xl mx-auto px-4 leading-relaxed">
          {subtitle}
        </p>
      )}
    </motion.div>
  );
}
